import React, { useEffect, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import styles from '../components/bodywon.module.css';
import { fetchMyOrders } from '../utils/orderApi';
import { fetchOrderItemsForOrder } from '../utils/orderItemApi';
import { ensureLoggedInUserId, isCartUserLoggedIn } from '../utils/cartApi';
import { useSettings } from '../context/SettingsContext';
import { formatOrderDate, formatOrderMoney, getOrderId } from '../admin/order/orderUtils';
import { enrichOrderItems } from './orderHistoryUtils';

const OrderProductThumb = ({ imageSrc, name }) => (
  <div className={styles.orderHistoryProductImage}>
    {imageSrc ? (
      <img src={imageSrc} alt={name || 'Product'} />
    ) : (
      <span className={styles.orderHistoryProductImagePlaceholder}>N/A</span>
    )}
  </div>
);

const OrderHistoryPage = ({ onRequireLogin }) => {
  const navigate = useNavigate();
  const { formatPrice } = useSettings();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
      return;
    }

    let active = true;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const userId = await ensureLoggedInUserId();
        if (!userId) {
          if (active) {
            setError('Please login again.');
            onRequireLogin?.();
          }
          return;
        }

        const { items } = await fetchMyOrders(userId, 50);
        if (!active) return;

        const ordersWithItems = await Promise.all(
          items.map(async (order) => {
            const orderId = getOrderId(order);
            try {
              const { items: orderItems } = await fetchOrderItemsForOrder(orderId);
              const enrichedItems = await enrichOrderItems(orderItems);
              return { order, items: enrichedItems };
            } catch {
              return { order, items: [] };
            }
          })
        );

        if (!active) return;
        setOrders(ordersWithItems);
      } catch (err) {
        if (active) setError(err.message || 'Could not load order history.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [navigate, onRequireLogin]);

  if (!isCartUserLoggedIn()) return null;

  return (
    <section className={styles.checkoutAddressPage}>
      <Container>
        <div className={styles.checkoutAddressHeader}>
          <h1 className={styles.checkoutAddressTitle}>Order History</h1>
          <p className={styles.checkoutAddressSub}>Your confirmed orders appear here.</p>
        </div>

        <div className={styles.checkoutAddressCard}>
          {loading ? (
            <div>Loading orders...</div>
          ) : error ? (
            <div className={styles.checkoutAddressError}>{error}</div>
          ) : orders.length === 0 ? (
            <div className={styles.orderHistoryEmpty}>
              <p>No orders yet.</p>
              <Button variant="dark" onClick={() => navigate('/')}>
                Start Shopping
              </Button>
            </div>
          ) : (
            <div className={styles.orderHistoryList}>
              {orders.map(({ order, items: orderItems }) => {
                const id = getOrderId(order);
                return (
                  <div key={id} className={styles.orderHistoryCard}>
                    <div className={styles.orderHistoryCardTop}>
                      <div>
                        <p className={styles.orderHistoryNumber}>
                          {order.OrderNumber ?? order.orderNumber ?? `Order #${id}`}
                        </p>
                        <p className={styles.orderHistoryDate}>
                          {formatOrderDate(order.OrderedAt ?? order.orderedAt)}
                        </p>
                      </div>
                      <span className={styles.orderHistoryStatus}>
                        {order.OrderStatus ?? order.orderStatus ?? 'Ordered'}
                      </span>
                    </div>

                    <div className={styles.orderHistoryProducts}>
                      <p className={styles.orderHistoryProductsTitle}>Products Ordered</p>
                      {orderItems.length === 0 ? (
                        <p className={styles.orderHistoryProductsEmpty}>No products found.</p>
                      ) : (
                        orderItems.map((item) => (
                          <div
                            key={item.orderItemId ?? `${item.productId}-${item.productName}`}
                            className={styles.orderHistoryProductRow}
                          >
                            <OrderProductThumb imageSrc={item.imageSrc} name={item.productName} />
                            <div className={styles.orderHistoryProductInfo}>
                              <span className={styles.orderHistoryProductName}>
                                {item.productName}
                              </span>
                              {item.sku ? (
                                <span className={styles.orderHistoryProductSku}>
                                  SKU: {item.sku}
                                </span>
                              ) : null}
                            </div>
                            <span className={styles.orderHistoryProductQty}>
                              Qty: {item.quantity}
                            </span>
                            <span className={styles.orderHistoryProductPrice}>
                              {formatPrice(item.totalPrice)}
                            </span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className={styles.orderHistoryCardBottom}>
                      <span className={styles.orderHistoryTotalLabel}>Total Paid</span>
                      <span className={styles.orderHistoryTotalValue}>
                        {formatOrderMoney(order.TotalAmount ?? order.totalAmount)}
                      </span>
                    </div>

                    <button
                      type="button"
                      className={styles.orderHistoryViewBtn}
                      onClick={() => navigate(`/account/orders/${id}`)}
                    >
                      View Full Details
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div className={styles.checkoutAddressListActions}>
            <Button variant="outline-secondary" type="button" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default OrderHistoryPage;
